import { useLLMNameSearch } from '@ltvco/refresh-lib/hooks';
import { Box, Stack } from '@ltvco/refresh-lib/theme';
import { ReportLoading } from '@ltvco/refresh-lib/v1';

import { CulturalFunFacts } from './CulturalFunFacts';
import { FamousPeople } from './FamousPeople';
import { HistoricalFunFacts } from './HistoricalFunFacts';
import { NameUsage } from './NameUsage';
import { SuggestedNames } from './SuggestedNames';

interface LLMNameDataProps {
  firstName: string;
  lastName: string;
  // eslint-disable-next-line no-unused-vars
  onNameSearch?: (firstName: string, lastName: string) => void;
}

interface LLMNameSearchData {
  culturalFunFacts?: string[];
  famousPeople?: Array<{ headline: string; blurb: string }>;
  genderAssociation?: string;
  globalUsage?: string;
  historicalFunFacts?: string[];
  suggestedNames?: Array<{ name: string; blurb: string; isSurname: boolean }>;
}

export function LLMNameData({
  firstName,
  lastName,
  onNameSearch,
}: LLMNameDataProps) {
  const name = `${firstName} ${lastName}`;
  const { data, isLoading } = useLLMNameSearch({
    name,
    enabled: !!name,
    customSuffixStorageKey: name,
  });

  const responseData = (data as any)?.data as LLMNameSearchData | undefined;
  const {
    culturalFunFacts,
    famousPeople,
    genderAssociation,
    globalUsage,
    historicalFunFacts,
    suggestedNames,
  } = responseData ?? {};

  const onNameClick = (name: string, isSurname: boolean) => {
    if (isSurname) {
      onNameSearch?.('', name);
    } else {
      onNameSearch?.(name, '');
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      {isLoading ? (
        <ReportLoading menuItems={5} />
      ) : (
        <>
          <Stack direction="row" spacing={2} sx={{ alignItems: 'stretch' }}>
            <Box sx={{ width: '70%' }}>
              <NameUsage
                genderAssociation={genderAssociation}
                globalUsage={globalUsage}
              />
            </Box>
            <Box sx={{ width: '30%' }}>
              <FamousPeople famousPeople={famousPeople} name={name} />
            </Box>
          </Stack>
          <Stack
            direction="row"
            spacing={2}
            sx={{ alignItems: 'stretch', marginTop: 2 }}
          >
            <Box sx={{ width: '50%' }}>
              <CulturalFunFacts culturalFunFacts={culturalFunFacts} />
            </Box>
            <Box sx={{ width: '50%' }}>
              <HistoricalFunFacts historicalFunFacts={historicalFunFacts} />
            </Box>
          </Stack>
          <Box sx={{ marginTop: 2 }}>
            <SuggestedNames
              suggestedNames={suggestedNames}
              onNameClick={onNameClick}
            />
          </Box>
        </>
      )}
    </>
  );
}
